import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  Chip,
  useMediaQuery,
} from "@mui/material";
import {
  CreditCard,
  CalendarToday,
  CheckCircle,
  WhatsApp,
  MenuBookOutlined,
  SmartDisplayOutlined,
  FolderZipOutlined,
  QueryStatsOutlined,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import Instructions from "./Instructions";
import VideoList from "./VideoList";
import FileDownloads from "./FileDownloads";
import TermometroFinanceiro from "./TermometroFinanceiro.js";
import { UserContext } from "../userContext/UserContext.js";
import { hasPaidAccess } from "../../../utils/access.js";
import {
  registrarSalesEvent,
  setPaymentEntrySource,
} from "../../../utils/salesEvents.js";

function Content({ isPaid, setCurrentView, tourActive, tourStep }) {
  const [activeTab, setActiveTab] = useState(0);
  const [offerVariant, setOfferVariant] = useState(null);
  const offerViewTrackedRef = useRef(false);
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const { valorPagamento, user, ofertaPagamento } = useContext(UserContext);
  const whatsappNumber = process.env.REACT_APP_WHATSAPP_NUMBER?.replace(/\D/g, "");
  const whatsappMessage =
    "Oi! Acabei de concluir o tour e quero mais informações sobre os indicadores.";
  const whatsappLink = whatsappNumber
    ? `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`
    : "";
  const usuarioPago = hasPaidAccess(isPaid);
  const isTestOffer = Boolean(ofertaPagamento?.isTestOffer);
  const accessDurationLabel = ofertaPagamento?.accessDurationLabel || "12 meses";
  const cardPaymentAllowed = ofertaPagamento?.cardPaymentAllowed;
  const offerLoaded =
    Number.isFinite(Number(valorPagamento)) &&
    Number(valorPagamento) > 0 &&
    typeof cardPaymentAllowed === "boolean";
  const valorCartaoNumerico = Number(ofertaPagamento?.cardAmount);
  const valorParcelaCartao =
    cardPaymentAllowed === true &&
    Number.isFinite(valorCartaoNumerico) &&
    valorCartaoNumerico > 0
      ? (valorCartaoNumerico / 2).toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        })
      : null;

  const handleChange = (event, newValue) => {
    if (!tourActive || usuarioPago) {
      setActiveTab(newValue);
    }
  };

  const handleRedirectToPayment = () => {
    setPaymentEntrySource("content_offer_primary");
    setCurrentView("pagamento");
  };
  const scrollToSalaAoVivo = () => {
    const section = document.getElementById("sala-ao-vivo-section");
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handlePrimaryCtaClick = () => {
    if (user?.id) {
      registrarSalesEvent(user.id, "cta_subscribe_click", {
        source: "user_panel",
        metadata: {
          location: "content_offer_primary",
          offer_variant: offerVariant || "A",
          amount: valorPagamento,
          is_test_offer: isTestOffer,
          access_duration: accessDurationLabel,
        },
      });
    }

    window.dispatchEvent(
      new CustomEvent("userpanel:offer-cta-click", {
        detail: { variant: offerVariant, cta: "primary" },
      })
    );
    handleRedirectToPayment();
  };

  const handleSecondaryCtaClick = () => {
    if (user?.id) {
      registrarSalesEvent(user.id, "user_whatsapp_open", {
        source: "content_offer",
      });
    }

    window.dispatchEvent(
      new CustomEvent("userpanel:offer-cta-click", {
        detail: { variant: offerVariant, cta: "secondary" },
      })
    );
  };

  // Simular clique nas abas durante o tour
  useEffect(() => {
    if (tourActive && tourStep > 0) {
      setActiveTab(tourStep - 1); // Ajusta a aba com base no tourStep
    }
  }, [tourStep, tourActive]);

  useEffect(() => {
    const handleNavigateTab = (event) => {
      if (event.detail?.tab === "videos") {
        setActiveTab(1);
      }
    };

    window.addEventListener("userpanel:navigate-tab", handleNavigateTab);
    return () =>
      window.removeEventListener("userpanel:navigate-tab", handleNavigateTab);
  }, []);

  useEffect(() => {
    if (tourActive || usuarioPago) return;

    const storageKey = "postTourOfferVariant";
    let variant = localStorage.getItem(storageKey);

    if (variant !== "A" && variant !== "B") {
      variant = Math.random() < 0.5 ? "A" : "B";
      localStorage.setItem(storageKey, variant);
    }

    setOfferVariant(variant);

    window.dispatchEvent(
      new CustomEvent("userpanel:offer-variant-view", {
        detail: { variant },
      })
    );
  }, [tourActive, usuarioPago]);

  useEffect(() => {
    if (
      tourActive ||
      usuarioPago ||
      !user?.id ||
      !offerVariant ||
      !offerLoaded ||
      offerViewTrackedRef.current
    ) {
      return;
    }

    offerViewTrackedRef.current = true;
    registrarSalesEvent(user.id, "offer_view", {
      source: "content_offer",
      metadata: {
        offer_variant: offerVariant,
        amount: valorPagamento,
        is_test_offer: isTestOffer,
        access_duration: accessDurationLabel,
        card_payment_allowed: cardPaymentAllowed,
      },
    });
  }, [
    accessDurationLabel,
    cardPaymentAllowed,
    offerLoaded,
    isTestOffer,
    offerVariant,
    tourActive,
    user?.id,
    usuarioPago,
    valorPagamento,
  ]);

  const offerContent = {
    A: {
      chip: isTestOffer
        ? `Teste de ${accessDurationLabel}`
        : "Acesso imediato após pagamento",
      title: isTestOffer
        ? "Teste os indicadores por tempo limitado"
        : "Desbloqueie todos os indicadores hoje",
      subtitle: isTestOffer
        ? `Acesso por ${accessDurationLabel} via Pix, sem parcelamento.`
        : cardPaymentAllowed
          ? "Assinatura anual com Pix ou cartão em até 2x."
          : "Assinatura anual com pagamento via Pix.",
      primaryLabel: isTestOffer ? "Quero testar agora" : "Assinar agora",
      secondaryLabel: "Quero mais informações",
      benefits: isTestOffer
        ? [
            "Liberação do acesso logo após confirmação",
            "Pagamento exclusivo via Pix",
            `Validade: ${accessDurationLabel} a partir da confirmação do pagamento`,
          ]
        : [
            "Acesso completo aos conteudos premium",
            cardPaymentAllowed
              ? "Pix ou cartão de crédito em até 2x"
              : "Pagamento exclusivo via Pix",
            `Validade: ${accessDurationLabel} a partir da confirmação do pagamento`,
          ],
    },
    B: {
      chip: isTestOffer
        ? `Oferta de teste • ${accessDurationLabel}`
        : "Oferta valida para novos acessos",
      title: isTestOffer
        ? "Comece com um teste e opere com mais confiança"
        : "Comece hoje e opere com mais confiança",
      subtitle: isTestOffer
        ? `Ative o teste de ${accessDurationLabel} e tenha os indicadores prontos para implementação.`
        : "Ative agora sua assinatura anual e tenha os indicadores prontos para implementação.",
      primaryLabel: "Quero liberar meu acesso",
      secondaryLabel: "Tenho dúvidas rápidas",
      benefits: isTestOffer
        ? [
            "Liberação do acesso logo após confirmação",
            "Aplicacao guiada para usar os indicadores",
            `Acesso por ${accessDurationLabel} via Pix`,
          ]
        : [
            "Liberação do acesso logo após confirmação",
            "Aplicacao guiada para usar os indicadores",
            cardPaymentAllowed
              ? "Pix ou cartão de crédito em até 2x"
              : "Pagamento exclusivo via Pix",
          ],
    },
  };

  const selectedOffer = offerContent[offerVariant] || offerContent.A;
  const tabItems = [
    {
      className: "tab-instrucoes",
      label: "Instruções",
      icon: <MenuBookOutlined fontSize="small" />,
      description: "Passo a passo para instalação e configuração dos indicadores.",
    },
    {
      className: "tab-videos",
      label: "Vídeos",
      icon: <SmartDisplayOutlined fontSize="small" />,
      description: "Trilha recomendada com aplicação prática para você aprender mais rápido.",
    },
    {
      className: "tab-arquivos",
      label: "Arquivos",
      icon: <FolderZipOutlined fontSize="small" />,
      description: "Biblioteca com atualizações mensais para manter tudo em dia.",
    },
    {
      className: "tab-termometro",
      label: "Termômetro",
      icon: <QueryStatsOutlined fontSize="small" />,
      description: "Leitura de tendência para apoiar decisões de operação.",
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        padding: isMobile ? 2 : 4,
        marginTop: isMobile ? "30px" : "20px",
        position: "relative",
        color: muiTheme.palette.text.primary,
      }}
    >
      {/* Mensagem de pagamento só aparece APÓS o tour e se o usuário não tiver pago */}
      {!tourActive && !usuarioPago && (
        <Box
          sx={{
            position: "fixed",
            top: isMobile ? "52%" : "34%",
            left: isMobile ? "50%" : "56%",
            transform: "translate(-50%, -50%)",
            zIndex: 10,
            width: isMobile ? "86vw" : 420,
            maxWidth: "92vw",
            background:
              "linear-gradient(180deg, rgba(5,11,18,0.96) 0%, rgba(2,7,12,0.98) 100%)",
            color: muiTheme.palette.text.primary,
            padding: isMobile ? "20px 18px" : "24px 24px",
            borderRadius: "18px",
            border: "1px solid rgba(255,255,255,0.12)",
            boxShadow: "0 18px 50px rgba(0, 0, 0, 0.45)",
            backdropFilter: "blur(8px)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -40,
              right: -40,
              width: 130,
              height: 130,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(76,175,80,0.22) 0%, rgba(76,175,80,0) 70%)",
              pointerEvents: "none",
            }}
          />

          <Chip
            label={selectedOffer.chip}
            size="small"
            sx={{
              mb: 1.5,
              fontWeight: 700,
              color: "#B8F5C1",
              bgcolor: "rgba(35, 174, 80, 0.16)",
              border: "1px solid rgba(72, 194, 112, 0.45)",
            }}
          />

          <Typography
            variant={isMobile ? "h5" : "h4"}
            component="h2"
            sx={{ mb: 0.6, fontWeight: 800, lineHeight: 1.1 }}
          >
            {selectedOffer.title}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.74)", mb: 2 }}
          >
            {selectedOffer.subtitle}
          </Typography>

          <Box
            sx={{
              p: 1.5,
              borderRadius: 2.5,
              bgcolor: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.09)",
              mb: 1.5,
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: "rgba(255,255,255,0.7)",
                letterSpacing: 1,
                fontWeight: 600,
                fontSize: "0.72rem",
              }}
            >
              {isTestOffer
                ? `Teste - ${accessDurationLabel}`
                : `Assinatura - ${accessDurationLabel}`}
            </Typography>

            <Typography
              sx={{
                mt: 0.4,
                fontSize: isMobile ? "1.65rem" : "1.85rem",
                fontWeight: 900,
                lineHeight: 1,
              }}
            >
              {valorPagamento !== null ? `R$ ${valorPagamento}` : "Carregando..."}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.72)", fontSize: "0.82rem", mt: 0.2 }}>
              no Pix
            </Typography>
            {valorParcelaCartao ? (
              <Box
                sx={{
                  mt: 1,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1,
                  py: 0.75,
                  borderRadius: "999px",
                  bgcolor: "rgba(73,149,255,0.14)",
                  border: "1px solid rgba(73,149,255,0.34)",
                }}
              >
                <CreditCard sx={{ fontSize: 15, color: "#BFD9FF" }} />
                <Typography sx={{ color: "#D7E8FF", fontSize: "0.82rem", fontWeight: 900 }}>
                  Cartão 2x de {valorParcelaCartao}
                </Typography>
              </Box>
            ) : null}
          </Box>

          <Typography
            variant={isMobile ? "body2" : "body1"}
            sx={{ display: "flex", alignItems: "center", mt: 0.8, gap: 1 }}
          >
            <CheckCircle sx={{ color: "#58D68D", fontSize: 18 }} />
            <span>{selectedOffer.benefits[0]}</span>
          </Typography>
          <Typography
            variant={isMobile ? "body2" : "body1"}
            sx={{ display: "flex", alignItems: "center", mt: 0.8, gap: 1 }}
          >
            {isTestOffer || !cardPaymentAllowed ? (
              <CalendarToday sx={{ fontSize: isMobile ? 16 : 18 }} />
            ) : (
              <CreditCard sx={{ fontSize: isMobile ? 16 : 18 }} />
            )}
            <strong>{selectedOffer.benefits[1]}</strong>
          </Typography>
          <Typography
            variant={isMobile ? "body2" : "body1"}
            sx={{ display: "flex", alignItems: "center", mt: 0.8, gap: 1 }}
          >
            <CalendarToday sx={{ fontSize: isMobile ? 16 : 18 }} />
            <strong>{selectedOffer.benefits[2]}</strong>
          </Typography>

          <Box sx={{ display: "flex", gap: 1.2, mt: 2.2, flexDirection: "column" }}>
            <Button
              variant="contained"
              color="success"
              onClick={handlePrimaryCtaClick}
              fullWidth
              sx={{
                textTransform: "none",
                fontWeight: 900,
                py: 1.2,
                fontSize: "1rem",
                borderRadius: 2.5,
                background: "linear-gradient(90deg, #37B85B 0%, #5DD06D 100%)",
                color: "#07250F",
                boxShadow: "0 8px 24px rgba(64, 192, 103, 0.35)",
                "&:hover": {
                  background: "linear-gradient(90deg, #41C968 0%, #70DC7D 100%)",
                },
              }}
            >
              {selectedOffer.primaryLabel}
            </Button>
            <Button
              variant="outlined"
              color="success"
              component="a"
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              disabled={!whatsappLink}
              onClick={handleSecondaryCtaClick}
              fullWidth
              startIcon={<WhatsApp />}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                py: 1.15,
                borderRadius: 2.5,
                borderColor: "rgba(93,208,109,0.55)",
                color: "#C6FFD0",
                "&:hover": {
                  borderColor: "#5DD06D",
                  bgcolor: "rgba(93,208,109,0.08)",
                },
              }}
            >
              {selectedOffer.secondaryLabel}
            </Button>
          </Box>
        </Box>
      )}

      <Box>
        <Box
          sx={{
            mb: 2.2,
            p: { xs: 1.5, md: 2 },
            borderRadius: 2.5,
            border: "1px solid rgba(255,255,255,0.1)",
            bgcolor: "rgba(14,22,33,0.72)",
          }}
        >
          <Typography sx={{ color: "rgba(244,249,255,0.96)", fontWeight: 800, mb: 0.4 }}>
            Área de Conteúdo
          </Typography>
          <Typography sx={{ color: "rgba(231,240,250,0.72)", fontSize: "0.92rem" }}>
            Acesse instruções, vídeos e arquivos organizados para acelerar sua implementação.
          </Typography>
        </Box>

        <Tabs
          value={activeTab}
          onChange={handleChange}
          indicatorColor="primary"
          textColor="primary"
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons={isMobile ? "auto" : false}
          sx={{
            marginBottom: 3,
            minHeight: 46,
            borderRadius: 2.5,
            bgcolor: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
            p: 0.5,
            "& .MuiTabs-indicator": {
              height: "100%",
              borderRadius: 2,
              background: "linear-gradient(90deg, rgba(46,120,230,0.25) 0%, rgba(78,154,253,0.22) 100%)",
            },
            "& .MuiTab-root": {
              fontSize: isMobile ? "12px" : "14px",
              minWidth: isMobile ? "80px" : "120px",
              padding: isMobile ? "8px" : "10px",
              minHeight: 38,
              zIndex: 1,
              textTransform: "none",
              fontWeight: 700,
              color: "rgba(231,240,250,0.72)",
              borderRadius: 2,
            },
            "& .Mui-selected": {
              color: "#EAF4FF",
            },
          }}
        >
          {tabItems.map((item) => (
            <Tab
              key={item.className}
              className={item.className}
              icon={item.icon}
              iconPosition="start"
              label={item.label}
            />
          ))}
        </Tabs>

        <Box
          sx={{
            // Ajusta o blur: sem blur se o usuário pagou ou durante o tour; blur reduzido (4px) durante o tour para não pagos; blur total (8px) se não pago e sem tour
            filter: tourActive || usuarioPago ? "none" : !usuarioPago && tourActive ? "blur(4px)" : "blur(8px)",
            pointerEvents: usuarioPago ? "auto" : "none",
            opacity: tourActive || usuarioPago ? 1 : 0.6,
            transition: "all 0.3s ease",
          }}
        >
          <Box
            sx={{
              mb: 1.6,
              p: { xs: 1.2, md: 1.4 },
              borderRadius: 2,
              border: "1px solid rgba(255,255,255,0.09)",
              bgcolor: "rgba(255,255,255,0.03)",
            }}
          >
            <Typography sx={{ color: "rgba(244,249,255,0.95)", fontWeight: 800, mb: 0.2 }}>
              {tabItems[activeTab]?.label}
            </Typography>
            <Typography sx={{ color: "rgba(231,240,250,0.72)", fontSize: "0.88rem" }}>
              {tabItems[activeTab]?.description}
            </Typography>
            {activeTab === 0 && (
              <Typography
                onClick={scrollToSalaAoVivo}
                sx={{
                  mt: 0.45,
                  fontSize: "0.8rem",
                  color: "rgba(189,210,242,0.74)",
                  cursor: "pointer",
                  display: "inline-block",
                  "&:hover": { color: "#CFE3FF" },
                }}
              >
                Informações sobre a sala
              </Typography>
            )}
          </Box>

          {activeTab === 0 && (
            <Instructions
              switchTab={setActiveTab}
              isPaid={isPaid}
              user={user}
            />
          )}
          {activeTab === 1 && <VideoList />}
          {activeTab === 2 && <FileDownloads />}
          {activeTab === 3 && <TermometroFinanceiro />}
        </Box>
      </Box>
    </Box>
  );
}

export default Content;
